/**
 * Utilidades criptográficas para derivación y verificación de PIN
 * Utiliza Web Crypto API con PBKDF2-SHA256
 * Sin dependencias externas; fallback si Web Crypto no disponible
 */

const PBKDF2_CONFIG = {
  iterations: 100000,
  version: 1,
};

/**
 * Genera un salt aleatorio de 16 bytes
 * @returns {Promise<string>} salt en base64
 */
export async function generateSalt() {
  // Feature-detect Web Crypto
  if (!globalThis.crypto || !globalThis.crypto.getRandomValues) {
    throw new Error('Web Crypto API no disponible en este navegador');
  }

  const saltBuffer = new Uint8Array(16);
  globalThis.crypto.getRandomValues(saltBuffer);
  return bufferToBase64(saltBuffer);
}

/**
 * Deriva un hash del PIN usando PBKDF2-SHA256
 * @param {string} pin - PIN en texto plano
 * @param {string} salt - salt en base64
 * @returns {Promise<{hash: string, salt: string, iterations: number, version: number}>}
 */
export async function hashPin(pin, salt) {
  // Feature-detect Web Crypto
  if (!globalThis.crypto?.subtle?.deriveBits) {
    throw new Error('Web Crypto SubtleCrypto no disponible en este navegador');
  }

  try {
    // Convertir salt de base64 a bytes
    const saltBuffer = base64ToBuffer(salt);

    // Importar el PIN como raw key
    const pinKey = await globalThis.crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(pin),
      'PBKDF2',
      false,
      ['deriveBits']
    );

    // Derivar bits usando PBKDF2-SHA256
    const derivedBits = await globalThis.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: saltBuffer,
        iterations: PBKDF2_CONFIG.iterations,
        hash: 'SHA-256',
      },
      pinKey,
      256 // 32 bytes = 256 bits
    );

    const hashBuffer = new Uint8Array(derivedBits);
    const hash = bufferToBase64(hashBuffer);

    return {
      hash,
      salt,
      iterations: PBKDF2_CONFIG.iterations,
      version: PBKDF2_CONFIG.version,
    };
  } catch (error) {
    console.error('[PIN_CRYPTO] Error hashing PIN:', error);
    throw error;
  }
}

/**
 * Verifica un PIN contra su hash almacenado
 * @param {string} pin - PIN en texto plano
 * @param {string} storedHash - hash almacenado en base64
 * @param {string} salt - salt almacenado en base64
 * @param {number} iterations - número de iteraciones usado (por defecto 100000)
 * @returns {Promise<boolean>}
 */
export async function verifyPin(pin, storedHash, salt, iterations = PBKDF2_CONFIG.iterations) {
  try {
    const result = await hashPin(pin, salt);
    
    // Comparación temporal constante (naive)
    // Para producción, usar crypto.subtle.timingSafeEqual si disponible
    return result.hash === storedHash;
  } catch (error) {
    console.error('[PIN_CRYPTO] Error verifying PIN:', error);
    return false;
  }
}

/**
 * Convierte un Uint8Array a string base64
 * @param {Uint8Array} buffer
 * @returns {string}
 */
function bufferToBase64(buffer) {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return globalThis.btoa(binary);
}

/**
 * Convierte un string base64 a Uint8Array
 * @param {string} base64
 * @returns {Uint8Array}
 */
function base64ToBuffer(base64) {
  const binary = globalThis.atob(base64);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer;
}

/**
 * Comprueba si Web Crypto está disponible
 * @returns {boolean}
 */
export function isWebCryptoAvailable() {
  return !!(globalThis.crypto?.subtle?.deriveBits && globalThis.crypto?.getRandomValues);
}
